"use client";

import { Phone, Users, Calendar, MessageSquare, Clock, TrendingUp, TrendingDown, DollarSign, Target } from "lucide-react";
import type { AgentActivityCardData } from "../../types/insurabot-cards";

const S = "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px, rgba(0,0,0,0.02) 0px 4px 12px -2px";

function TrendBadge({ value }: { value: number }) {
  if (value === 0) return null;
  const isUp = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] font-mono font-bold ${isUp ? "text-emerald-600" : "text-red-500"}`} style={{ fontFeatureSettings: '"tnum"' }}>
      {isUp ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
      {isUp ? "+" : ""}{Math.round(value)}%
    </span>
  );
}

export default function AgentActivityCard({ data }: { data: AgentActivityCardData }) {
  const t = data.today;
  const d = data.dashboard;

  return (
    <div className="mt-3 rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ boxShadow: S }}>
      {/* Today's Activity */}
      <div className="bg-white px-4 py-3">
        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em] mb-3">Today's Activity</p>
        <div className="grid grid-cols-5 gap-2">
          {[
            { icon: Phone, label: "Calls", value: t.calls, color: "text-violet-500", bg: "bg-violet-50" },
            { icon: Clock, label: "Dial Time", value: `${t.dialMinutes}m`, color: "text-sky-500", bg: "bg-sky-50" },
            { icon: Users, label: "Leads", value: t.leadsContacted, color: "text-emerald-500", bg: "bg-emerald-50" },
            { icon: Calendar, label: "Appts", value: t.appointments, color: "text-amber-500", bg: "bg-amber-50" },
            { icon: MessageSquare, label: "SMS", value: t.smsSent, color: "text-pink-500", bg: "bg-pink-50" },
          ].map(({ icon: Icon, label, value, color, bg }) => (
            <div key={label} className="text-center">
              <div className={`h-8 w-8 rounded-lg ${bg} flex items-center justify-center mx-auto mb-1`}>
                <Icon className={`h-3.5 w-3.5 ${color}`} />
              </div>
              <p className="text-sm font-mono font-black text-gray-900 leading-none" style={{ fontFeatureSettings: '"tnum"' }}>{value}</p>
              <p className="text-[7px] text-gray-400 font-medium mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Agency Trends (if dashboard data available) */}
      {d && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
          <p className="text-[8px] font-bold text-gray-400 uppercase tracking-[0.1em] mb-2">This Month</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {[
              { label: "Premium", value: d.totalPremiumSold ? `$${(d.totalPremiumSold.value || 0).toLocaleString()}` : "—", trend: d.totalPremiumSold?.changePercentage, icon: DollarSign },
              { label: "Policies", value: d.policiesSold?.value ?? "—", trend: d.policiesSold?.changePercentage, icon: Target },
              { label: "Leads", value: d.activeLeads?.value ?? "—", trend: d.activeLeads?.changePercentage, icon: Users },
              { label: "Appointments", value: d.appointments?.value ?? "—", trend: d.appointments?.changePercentage, icon: Calendar },
            ].map(({ label, value, trend, icon: Icon }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon className="h-3 w-3 text-gray-400" />
                <div className="flex-1">
                  <p className="text-[10px] text-gray-500">{label}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono font-bold text-gray-900" style={{ fontFeatureSettings: '"tnum"' }}>{value}</span>
                  {trend != null && <TrendBadge value={trend} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agency stats */}
      {data.agency && (
        <div className="bg-white px-4 py-2.5 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[8px] text-gray-400">
            {data.agency.agentCount} agents · {Math.round(data.agency.closingRatio * 100)}% close rate
          </span>
          <span className="text-[8px] font-mono font-bold text-gray-500" style={{ fontFeatureSettings: '"tnum"' }}>
            ${Math.round(data.agency.avgPolicySize).toLocaleString()} avg policy
          </span>
        </div>
      )}
    </div>
  );
}
